/**
 * @fileoverview Hook for managing account lists and selected list state
 * @module hooks/useAccountLists
 *
 * Provides TanStack Query-based fetching for account lists and a persistent
 * selected list state stored in localStorage.
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiClient from '../lib/apiClient';
import logger from '../lib/logger';
import { useUserPreferences } from './useUserPreferences';

const LISTS_KEY = ['account-lists'];
const SELECTED_LIST_KEY = 'selectedAccountListId';
// Cross-device persistence key in user_preferences. Stored object-wrapped as
// { list_id } so a cleared selection ({ list_id: null }) still round-trips —
// the backend's PUT /preferences/:type rejects a bare null value.
const ACTIVE_LIST_PREF = 'active_list_id';

/**
 * Hook for fetching and managing account lists
 * @returns {Object} Account lists state and handlers
 */
export function useAccountLists() {
  // Selected list — seeded from localStorage for an instant, no-flash startup
  // and used as the offline fallback. user_preferences is the cross-device
  // source of truth and reconciles in once it loads (see hydration effect).
  const [selectedListId, setSelectedListId] = useState(() => {
    const saved = localStorage.getItem(SELECTED_LIST_KEY);
    return saved ? parseInt(saved, 10) : null;
  });

  const {
    preferences,
    loading: prefsLoading,
    updatePreference,
  } = useUserPreferences();

  // Persist the active list to user_preferences (cross-device). localStorage is
  // still written by the effect below; this adds server-side durability.
  const persistActiveList = useCallback((listId) => {
    try {
      updatePreference(ACTIVE_LIST_PREF, { list_id: listId ?? null });
    } catch (err) {
      // Never let a preference write break selection — localStorage covers us.
      logger.accountLists.warn('persist active_list_id failed', { listId, error: err?.message });
    }
  }, [updatePreference]);

  // Hydrate the server-stored selection exactly once, after prefs finish
  // loading. If the server has a value (including an explicit null from a
  // clear on another device) it wins. If the server has never stored the
  // pref, migrate the current localStorage selection up to the server.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || prefsLoading) return;
    hydratedRef.current = true;

    const hasServerPref = preferences
      && Object.prototype.hasOwnProperty.call(preferences, ACTIVE_LIST_PREF);

    if (hasServerPref) {
      const serverId = preferences[ACTIVE_LIST_PREF]?.list_id ?? null;
      setSelectedListId((current) => {
        if (serverId !== current) {
          logger.accountLists.info('active list hydrated from preferences', {
            serverId, previousLocal: current,
          });
        }
        return serverId;
      });
    } else if (selectedListId != null) {
      // First run after this feature shipped — push local up to the server.
      logger.accountLists.info('migrating local active list to preferences', { listId: selectedListId });
      persistActiveList(selectedListId);
    }
  }, [prefsLoading, preferences, selectedListId, persistActiveList]);

  // Fetch all lists
  const listsQuery = useQuery({
    queryKey: LISTS_KEY,
    queryFn: () => apiClient.get('/api/account-lists'),
    staleTime: 60000, // 1 minute
  });

  // Fetch selected list details (including members)
  const selectedListQuery = useQuery({
    queryKey: ['account-list', selectedListId],
    queryFn: () => apiClient.get(`/api/account-lists/${selectedListId}`),
    enabled: !!selectedListId,
    staleTime: 60000,
  });

  // Persist selection to localStorage
  useEffect(() => {
    if (selectedListId) {
      localStorage.setItem(SELECTED_LIST_KEY, selectedListId.toString());
    } else {
      localStorage.removeItem(SELECTED_LIST_KEY);
    }
  }, [selectedListId]);

  // Select a list (persists to localStorage via effect + user_preferences)
  const selectList = useCallback((listId) => {
    setSelectedListId(listId);
    persistActiveList(listId);
  }, [persistActiveList]);

  // Clear selection (persists the cleared state cross-device)
  const clearList = useCallback(() => {
    setSelectedListId(null);
    persistActiveList(null);
  }, [persistActiveList]);

  // Get the selected list object from the lists array
  const selectedList = useMemo(() => {
    if (!selectedListId || !listsQuery.data) return null;
    return listsQuery.data.find(l => l.id === selectedListId) || null;
  }, [selectedListId, listsQuery.data]);

  // Get members with matched department_ids (for filtering)
  const selectedMembers = useMemo(() => {
    if (!selectedListQuery.data?.members) return [];
    return selectedListQuery.data.members.filter(m => m.department_id != null);
  }, [selectedListQuery.data]);

  // Get department IDs from selected list for filtering
  const selectedDepartmentIds = useMemo(() => {
    return selectedMembers.map(m => m.department_id);
  }, [selectedMembers]);

  return {
    // Lists
    lists: listsQuery.data || [],
    listsLoading: listsQuery.isLoading,
    listsError: listsQuery.error,

    // Selected list
    selectedListId,
    selectedList,
    selectedListDetail: selectedListQuery.data,
    selectedListLoading: selectedListQuery.isLoading,

    // Members from selected list
    selectedMembers,
    selectedDepartmentIds,

    // Actions
    selectList,
    clearList,
  };
}

export default useAccountLists;
